import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { invalidateJobPaymentQueries } from '@/hooks/jobs/useJobs';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { auditLogKeys, contactKeys, invoiceKeys, paymentKeys } from '@/lib/queryKeys';
import type { PaymentRecord } from './usePayments';
import { refundFailureMessage } from './useRefundPayment';

export interface CancelBankPaymentInput {
  id: string;
  invoiceId?: string | null;
  contactId?: string | null;
  /** The job page shows this payment and its balance; refresh it too. */
  jobId?: string | null;
  reason?: string;
  /** One per dialog: a retried click replays the same cancel. */
  idempotencyKey: string;
}

/**
 * Stop a bank debit that is still clearing. The processor voids it and the
 * payment fails, so the customer owes the amount again. Once the debit has
 * gone to the bank this is refused, and the money comes back as a refund
 * after it settles.
 */
export function useCancelBankPayment() {
  const queryClient = useQueryClient();
  return useApiMutation<PaymentRecord, CancelBankPaymentInput>({
    mutationFn: ({ id, reason, idempotencyKey }) =>
      apiPost<PaymentRecord, { reason?: string }>(
        operationsClient,
        invoiceEndpoints.cancelBankPayment(id),
        reason ? { reason } : {},
        { headers: { 'Idempotency-Key': idempotencyKey } },
      ),
    onSuccess: (_payment, variables) => {
      toast.success('Bank payment cancelled. The customer owes this amount again.');
      void queryClient.invalidateQueries({ queryKey: paymentKeys.all() });
      void queryClient.invalidateQueries({
        queryKey: auditLogKeys.byEntity('payment', variables.id),
      });
      if (variables.invoiceId) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(variables.invoiceId) });
        void queryClient.invalidateQueries({
          queryKey: [...invoiceKeys.detail(variables.invoiceId), 'payments'],
        });
      }
      if (variables.contactId) {
        void queryClient.invalidateQueries({ queryKey: contactKeys.summary(variables.contactId) });
        void queryClient.invalidateQueries({
          queryKey: contactKeys.paymentsBase(variables.contactId),
        });
      }
      if (variables.jobId) invalidateJobPaymentQueries(queryClient, variables.jobId);
    },
    onError: (error) => {
      // The server's own message says why (already sent to the bank, not a
      // clearing bank payment); the fallback never claims nothing happened.
      toast.error(refundFailureMessage(error));
    },
    showErrorToast: false,
  });
}
